import { extendLoginToken } from '@api/api';
import { addModal, removeModal } from '@store/actions/commonAction';
import store from '@store/store';
import { isEmptyString, isNil } from '.';

// 로그인 토큰 만료 시간  (180분-->60분)
export const LOGIN_TOKEN_DURATION = 60 * 60 * 1000;

// 로그인 만료시간 알림 확인창 (10분)
export const LOGIN_EXPIRE_ALERT_TIME = 10 * 60 * 1000;

export const extendLoginTimer = async (options = {}) => {
  const { isShadowUpdate = false } = options;

  const isSuccess = await requestExtendLoginTime(isShadowUpdate);
  if (!isSuccess) await askRetry();

  async function requestExtendLoginTime(isShadowUpdate) {
    try {
      // 로그인 시간 연장을 위해 홈 메뉴 이동 기록 API 호출
      const response = await extendLoginToken({
        isLoading: !isShadowUpdate,
      });
      const isSuccess = $utils.checkApiSuccess(response);

      return isSuccess;
    } catch (error) {
      return false;
    }
  }

  function askRetry() {
    return new Promise(resolve => {
      store.dispatch(
        addModal({
          message: '로그인 시간 연장에 실패하였습니다.<br/>재시도하시겠습니까?',
          okLabel: '재시도',
          onClose: async isOk => {
            if (isOk) await extendLoginTimer(options);
            return resolve();
          },
        }),
      );
    });
  }
};

/**
 * 로그인 Interaction 시간 갱신 설정 및 초기화
 * @return {void}
 * @author Robin
 */
export const initLoginTimeEvent = () => {
  const interactions = [
    'click',
    'touchstart',
    'keydown',
    'mousemove',
    'scroll',
  ];

  interactions.forEach(eventName => {
    window.addEventListener(eventName, saveLastInteraction, false);
  });

  // 이전 마지막 인터렉션 시간 제거
  removeLastInteration();

  // 로그인 시작 시간 저장
  saveStartLoginTime();

  // 로그인 세션 시간 서버 연장
  extendLoginTimer({ isShadowUpdate: true });
};

/**
 * 로그인 Interaction 시간 갱신 설정 제거
 * @return {void}
 * @author Robin
 */
export const clearLoginTimeEvent = () => {
  const interactions = [
    'click',
    'touchstart',
    'keydown',
    'mousemove',
    'scroll',
  ];

  interactions.forEach(eventName => {
    window.removeEventListener(eventName, saveLastInteraction, false);
  });

  // 이전 마지막 인터렉션 시간 제거
  removeLastInteration();

  // 세션 시작 시간 제거
  removeStartLoginTime();
};

/**
 * 로그인 세션 시작 시간 저장
 * @return {void}
 * @author Robin
 */
export const saveStartLoginTime = () => {
  const currentTime = Date.now();
  localStorage.setItem('login_start_time', currentTime);
};

/**
 * 로그인 세션 시작 시간 불러오기
 * @return {number}
 * @author Robin
 */
export const loadStartLoginTime = () => {
  const savedStartLoginTime = localStorage.getItem('login_start_time');
  if (isEmptyString(savedStartLoginTime)) return 0;

  const startLoginTime = new Date(Number(savedStartLoginTime)).getTime();
  return startLoginTime;
};

/**
 * 로그인 세션 시작 시간 제거
 * @return {void}
 * @author Robin
 */
export const removeStartLoginTime = () => {
  localStorage.removeItem('login_start_time');
};

/**
 * 로그인 Interaction 시간 갱신
 * @param {number} startLoginTime 저장할 로그인 시작 시간
 * @return {void}
 * @author Robin
 */
export const saveLastInteraction = () => {
  const currentTime = Date.now();
  localStorage.setItem('last_interaction', currentTime);

  if (window.isExpireAlert) {
    window.isExpireAlert = false;

    // 연장팝업 닫기
    if (window._hideIndex) store.dispatch(removeModal(window._hideIndex));
    extendLoginTimer({ isShadowUpdate: true });
  }
};

/**
 * 로그인 Interaction 시간 로딩
 * @return {void}
 * @author Robin
 */
export const loadLastInteraction = () => {
  const savedLastInteraction = localStorage.getItem('last_interaction');
  if (isEmptyString(savedLastInteraction)) return 0;

  const lastInteraction = new Date(Number(savedLastInteraction)).getTime();
  return lastInteraction;
};

/**
 * 로그인 Interaction 시간 제거
 * @return {void}
 * @author Robin
 */
export const removeLastInteration = () => {
  localStorage.removeItem('last_interaction');
};

/**
 * 마지막 Interaction 시간을 바탕으로 현재 보여줄 로그인 세션 시간 계산
 * @param {number} loginTime 현제 로그인 세션 시간
 * @return {number} 변경될 로그인 세션 시간
 * @author Robin
 */
export const calculateLoginTime = (loginTime = 0) => {
  const currentTime = Date.now();
  const lastInteraction = loadLastInteraction();

  // 마지막 로그인 Interaction 시간이 없으면 로그인 -1초 해서 노출
  if (isNil(lastInteraction) || lastInteraction <= 0) return loginTime - 1000;

  const loginSessiontEndTime = lastInteraction + LOGIN_TOKEN_DURATION;
  const loginSessionTime = Math.min(
    Math.ceil(loginSessiontEndTime - currentTime, LOGIN_TOKEN_DURATION) + 1,
  );

  return loginSessionTime;
};

/**
 * 로그인 세션 시작 시간과 마지막 Interaction 시간 등을 확인하여
 * 로그인 세션 시간 자동연장 가능여부 체크 및 연장시키는 함수
 * @return {void}
 * @author Robin
 */
export const checkLoginAutoExtend = () => {
  const currentTime = Date.now();
  const loginStartTime = loadStartLoginTime();
  const expireAlertDuration = LOGIN_TOKEN_DURATION - LOGIN_EXPIRE_ALERT_TIME;
  const autoExtendTime = loginStartTime + expireAlertDuration;
  const isAutoExtendTime = autoExtendTime <= currentTime;
  if (!isAutoExtendTime) return;

  // 로그인 이후에 사용자 Interaction 시간 확인하여, 로그인 시간 자동연장 여부 확인
  const lastInteraction = loadLastInteraction();
  const isLoginAutoExtend = lastInteraction >= loginStartTime;

  if (isLoginAutoExtend) {
    extendLoginTimer({ isShadowUpdate: true });
    saveStartLoginTime();
  }
};
